import React from 'react';
import I18n from 'i18n-js';
import { Filter } from './search_filters';
import FilterChip from './filter_chip';
import { useUser, useExpEnabled } from '../user_hooks';
import MarketplaceFilter from './marketplace_filter';
import MarketplaceDismissibleFilterRow from './marketplace_dismissible_filter_row';
import { Location } from 'history';
import { isSomethingSelected } from '../facet_utils';
import experiments from '../experiments';
import MarketplaceFilterSidebar from './marketplace_filter_sidebar';
import Popover from './popover';
import SelectFacet from './select_facet';
import { reverb_search_Filter_WidgetType as WidgetType } from '../rql';
import { experimentInt } from '../user_context_helpers';

interface CSP2FiltersOption {
  name: string;
  total: number;
  selected: boolean;
  optionValue?: string;
  setValues?: any;
  unsetValues?: any;
  paramName?: string;
}
interface IProps {
  filters?: Filter[];
  loading?: boolean;
  trackingQuery?: string;
  displayCount?: boolean;
  dynamicTitle?: boolean;
  hideOnMobile?: boolean;
  location: Location;
  totalText?: string;
  finishOptions?: CSP2FiltersOption[];
}

export const FEATURED = [
  'CONDITION_SLUGS',
  'CATEGORY_SLUGS',
  'BRAND_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
  'LOCATION',
];

export const MODEL_FAMILY_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

export function excludedFilters(pathname) {
  const EXCLUDED = ['SORT_BY'];
  if (pathname?.includes('/marketplace')) {
    EXCLUDED.push('TEXT_QUERY');
  }

  if (pathname?.includes('/shop')) {
    EXCLUDED.push('SELLER_LOCATION');
  }

  return EXCLUDED;
}
const CSP_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
  '6a24e2dc-800d-4f06-ac67-acef9569a40f', // fretboard material filter
];

const ALWAYS_SHOW_SELLER_LOCATION_FILTER_REGIONS = [
  'GB',
  'AU',
];

export function isDefaultedSellerLocationFilter(filterKey, user) {
  return filterKey === 'SELLER_LOCATION' && ALWAYS_SHOW_SELLER_LOCATION_FILTER_REGIONS.includes(user.shippingRegionCode);
}

export default function MarketplaceFilterRow({
  filters = [],
  loading = false,
  trackingQuery = '',
  displayCount = false,
  dynamicTitle = false,
  hideOnMobile = false,
  location = null,
  totalText = '',
  finishOptions = [],
}: IProps) {
  const user = useUser();
  const pathname = location?.pathname;
  const inMarketplaceDismissibleFiltersExp = useExpEnabled(experiments.MARKETPLACE_DISMISSIBLE_FILTERS);

  if (loading && filters.length === 0) {
    return (
      <div className="search-overview__filters">
        <Placeholder />
      </div>
    );
  }

  const includedFilters = filters.filter(f => {
    const key = getFilterKey(f);
    return !excludedFilters(pathname).includes(key);
  });

  const defaultFilters = getFilters(includedFilters, user, pathname);

  const defaultFilterKeys = defaultFilters.map(f => {
    return f.aggregationName ? f.aggregationName : f.key;
  });

  const otherSelectedFilters = includedFilters.filter(f => {
    const key = getFilterKey(f);
    if (defaultFilterKeys.includes(key)) return false;
    return f.options && f.options.some(o => {
      if (o.all) return false;
      // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
      if (key === 'SHIPS_TO' && o.optionValue === user.shippingRegionCode) return false;
      return o.selected;
    });
  });

  const allSelectedFilters = includedFilters.filter(f => {
    const key = f.aggregationName || f.key;

    // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
    if (key === 'SHIPS_TO') {
      if (f.options.some(o => {
        return o.optionValue === user.shippingRegionCode;
      })) return false;
      return true;
    }
    return isSomethingSelected(f, true);
  });

  const finishFilter = buildFinishFilter(finishOptions);

  if (isSomethingSelected(finishFilter, true)) {
    allSelectedFilters.push(finishFilter);
  }

  return (
    <>
      <div className="search-overview__filters">
        {defaultFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
            />
          );
        })}
        {dynamicTitle && otherSelectedFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
            />
          );
        })}
        <FinishFilter
          loading={loading}
          filter={finishFilter}
          trackingQuery={trackingQuery}
          dynamicTitle={!inMarketplaceDismissibleFiltersExp}
          location={location}
        />
        <MarketplaceFilterSidebar
          filters={[finishFilter, ...filters]}
          loading={loading}
          subtitle={totalText}
          trackingQuery={trackingQuery}
          location={location}
        />
      </div>
      { inMarketplaceDismissibleFiltersExp &&
        <div className="search-overview__mobile-overflow">
          <div className="search-overview__filters search-overview__filters--dismissible">
            <MarketplaceDismissibleFilterRow
              key={'dismissible-filters'}
              filters={allSelectedFilters}
              location={location}
            />
          </div>
        </div>
      }
    </>
  );
}

function Placeholder() {
  return (
    <div className="marketplace-filter">
      <FilterChip loading>
        <div className="filter-chip__loading-placeholder" />
      </FilterChip>
    </div>
  );
}

function getFilterKey(f: Filter) {
  return f.key || f.aggregationName;
}

function getFeaturedFilterKeys(pathname) {
  if (pathname?.includes('/f/')) return MODEL_FAMILY_FEATURED;
  if (pathname?.includes('/p/')) return CSP_FEATURED;
  return FEATURED;
}

function getFilters(filters, user, pathname) {
  const shopLocalExpBucket = experimentInt(user, experiments.SHOP_LOCAL_V2);
  const featuredFilterKeys = getFeaturedFilterKeys(pathname);
  const featuredFilters = filters.filter(f => {
    const key = getFilterKey(f);
    if (shopLocalExpBucket === 0 && key === 'LOCATION') return false;
    return featuredFilterKeys.includes(key) || isDefaultedSellerLocationFilter(key, user);
  });

  if (featuredFilters.length === 0) {
    return filters.slice(0, 5);
  }

  return featuredFilters.sort((a: Filter, b: Filter) => {
    const keyA = getFilterKey(a);
    const keyB = getFilterKey(b);
    const indexOfA = featuredFilterKeys.indexOf(keyA);
    const indexOfB = featuredFilterKeys.indexOf(keyB);
    if (indexOfA < indexOfB) { return -1; }
    if (indexOfA > indexOfB) { return 1; }
    return 0;
  });
}

function buildFinishFilter(finishOptions: CSP2FiltersOption[]): Filter {
  const finishFilter = {
    name: 'Finish',
    widgetType: WidgetType.RADIO,
    aggregationName: 'finish',
    key: 'finishOptions',
    options: [],
  };

  if (!finishOptions || finishOptions.length <= 1) return finishFilter;

  const selected = finishOptions.find(opt => opt.selected);

  const allFinishesOption = {
    name: I18n.t('commons.marketplaceFilters.allFinishes'),
    selected: !selected,
    optionValue: '',
    setValues: [],
    unsetValues: [],
    paramName: 'canonicalFinish',
    all: true,
  };

  return {
    ...finishFilter,
    options: [
      allFinishesOption,
      ...finishOptions.sort((a, b) => {
        return b.total - a.total;
      }).map(option => {
        return {
          ...option,
          count: {
            value: option.total,
          },
        };
      }),
    ],
  };
}

function FinishFilter({ loading, filter, trackingQuery, dynamicTitle, location }) {
  if (!filter) return null;
  if (filter.options?.length <= 1) return null;
  if (!location?.pathname.includes('/p/')) return null;

  const selected = filter.options.find(opt => opt.selected && !opt.all);

  return (
    <div className="marketplace-filter marketplace-filter--mobile-hidden">
      <Popover
        trigger={
          <FilterChip
            withDropdown
            loading={loading}
            selected={selected}
          >
            {(dynamicTitle && selected) && selected.name}
            {(!dynamicTitle || !selected) && filter.name}
          </FilterChip>
        }
        loading={loading}
      >
        <SelectFacet
          pageSection="filter-row"
          filter={filter}
          trackingQuery={trackingQuery}
        />
      </Popover>
    </div>
  );
}
